import { makeAutoObservable } from 'mobx';

import { AnalyticDimensions } from '@/analytics/types';
import { DriveItemType } from '@/drive/entities/DriveItem/DriveItem';
import { ProductItem } from '@/drive/entities/DriveItem/ProductItem';
import { TransientDriveItem } from '@/drive/entities/transientDriveItem';
import { DriveItemRequest } from '@/drive/types/DriveItemRequest';
import { ShareActionType, SocialShareActionType } from '@/forwarding/constants/ShareActionType';
import { PinnedItem } from '@/forwarding/entities/PinnedItem/PinnedItem';
import { ShareConfig } from '@/forwarding/entities/ShareConfig/ShareConfig';
import { createShareInfoText, createSocialShareUrl, getMailSubject } from '@/forwarding/entities/ShareConfig/ShareConfig.utils';
import { ForwardInfo } from '@/forwarding/types/ForwardInfo';
import { NativeShareConfig } from '@/forwarding/types/NativeShareConfig';
import { MailUrlParams } from '@/forwarding/utils/createMailUrl';

export class PinnedItemsShareConfig implements ShareConfig {
  readonly hasMultipleUrls: boolean;

  constructor(
    private readonly pinnedItems: PinnedItem[],
    private readonly getInfo: () => ForwardInfo,
    private readonly getDtoItems: () => Nullable<TransientDriveItem[]>,
  ) {
    makeAutoObservable<PinnedItemsShareConfig, 'canBeSendToSpectrum' | 'pinnedItems' | 'getInfo' | 'getDtoItems'>(
      this,
      {
        hasMultipleUrls: false,
        info: false,
        canBeSendToSpectrum: false,
        pinnedItems: false,
        getInfo: false,
        getDtoItems: false,
      },
    );

    this.hasMultipleUrls = pinnedItems.length > 1;
  }

  get info(): ForwardInfo {
    return this.getInfo();
  }

  getUrl(): Nullable<string> {
    return null;
  }

  getSocialShareUrl(type: SocialShareActionType): Nullable<string> {
    return type === ShareActionType.WhatsApp ? createSocialShareUrl(type, this.linksText) : null;
  }

  getCopyText(): string {
    return this.linksText;
  }

  getInfoText(): string {
    return createShareInfoText(this.info.title, this.info.description, this.linksText);
  }

  getMailUrlParams(fromEmail = ''): Nullable<MailUrlParams> {
    const separator = this.hasMultipleUrls ? '\n' : ' ';

    return {
      from: fromEmail,
      body: `Please take a look at${separator}${this.linksText}`,
      subject: getMailSubject(this.info.title),
    };
  }

  getNativeShareConfig(): Nullable<NativeShareConfig> {
    return null;
  }

  getAddToSpectrumRequests(): Nullable<DriveItemRequest[]> {
    const dtoItems = this.getDtoItems();

    if (
      dtoItems &&
      dtoItems.length > 0 &&
      dtoItems.length === this.pinnedItems.length &&
      dtoItems.every(this.canBeSendToSpectrum)
    ) {
      return dtoItems.map((product) => ({
        type: DriveItemType.Spectrum,
        productId: product.productId,
      }));
    }

    return null;
  }

  getAnalyticsDimensions(): Nullable<AnalyticDimensions> {
    return null;
  }

  private get linksText(): string {
    return this.pinnedItems.map((item) => item.getUrl()).join('\n');
  }

  private canBeSendToSpectrum = (item: TransientDriveItem): item is ProductItem => {
    return item instanceof ProductItem && item.hasCBR;
  };
}
